
import React from 'react';

interface JokerArtProps {
  id: string;
  size?: 'sm' | 'lg';
  rarity: string;
}

const JokerArt: React.FC<JokerArtProps> = ({ id, size = 'sm', rarity }) => {
  const isLarge = size === 'lg';
  
  // Mapping of Joker IDs to their specific visual representation
  const renderIllustration = () => {
    switch (id) {
      case 'j_1': // Second Chance - Green Rewind
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="absolute w-2/3 h-2/3 border-4 border-emerald-400 rounded-full animate-pulse opacity-20" />
            <svg viewBox="0 0 24 24" className="w-1/2 h-1/2 text-emerald-400 fill-current">
              <path d="M12,5V1L7,6L12,11V7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12H5A7,7 0 0,0 12,19A7,7 0 0,0 19,12A7,7 0 0,0 12,5Z" />
            </svg>
          </div>
        );
      case 'j_2': // Hot Hand - Flames
        return (
          <div className="relative w-full h-full flex items-center justify-center">
             <div className="absolute bottom-2 w-1/2 h-2/3 bg-gradient-to-t from-orange-600 to-yellow-300 rounded-full blur-md animate-bounce" />
             <svg viewBox="0 0 24 24" className="w-2/3 h-2/3 text-orange-500 fill-current relative z-10">
                <path d="M17.55,11.2C17.32,10.9 17.05,10.64 16.79,10.38C15.03,8.58 15,6.03 15,6.03C15,6.03 13.5,7.5 12.35,9.73C11.16,11.96 11.24,14.44 11.24,14.44C11.24,14.44 10.15,13.5 9.4,11.66C8.66,9.81 9.07,7.85 9.07,7.85C9.07,7.85 6.42,10.27 6.06,14.41C5.64,19.25 9.04,22 12,22C14.96,22 18.5,19.8 18.5,15.5C18.5,13.87 18.15,12.4 17.55,11.2Z" />
             </svg>
          </div>
        );
      case 'j_3': // Glass Brain - Shattered Blue
        return (
          <div className="relative w-full h-full flex items-center justify-center bg-blue-900/20">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-1 left-2 w-full h-px bg-blue-300/40 rotate-45" />
                <div className="absolute top-1/2 left-0 w-full h-px bg-blue-300/40 -rotate-12" />
                <div className="absolute bottom-2 right-1 w-full h-px bg-blue-300/40 rotate-[120deg]" />
            </div>
            <svg viewBox="0 0 24 24" className="w-1/2 h-1/2 text-blue-300 fill-current drop-shadow-[0_0_8px_rgba(147,197,253,0.8)]">
              <path d="M18,17C18,17.55 17.55,18 17,18C16.45,18 16,17.55 16,17H18M12,2C11.5,2 11,2.5 11,3C11,3.5 11.5,4 12,4V2M8,17C8,17.55 7.55,18 7,18C6.45,18 6,17.55 6,17H8M12,18C12,18.55 11.55,19 11,19C10.45,19 10,18.55 10,18H12M12,4C11.5,4 11,4.5 11,5C11,5.5 11.5,6 12,6V4M12,6C11.5,6 11,6.5 11,7C11,7.5 11.5,8 12,8V6M12,8C11.5,8 11,8.5 11,9C11,9.5 11.5,10 12,10V8M12,10C11.5,10 11,10.5 11,11C11,11.5 11.5,12 12,12V10M12,12C11.5,12 11,12.5 11,13C11,13.5 11.5,14 12,14V12M12,14C11.5,14 11,14.5 11,15C11,15.5 11.5,16 12,16V14M12,16C11.5,16 11,16.5 11,17C11,17.5 11.5,18 12,18V16Z" />
            </svg>
          </div>
        );
      case 'j_4': // Meditation - Zen Circles
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="w-2/3 h-2/3 border-2 border-teal-400 rounded-full animate-[spin_8s_linear_infinite]" />
            <div className="absolute w-1/3 h-1/3 border-2 border-teal-200 rounded-full animate-[spin_4s_linear_infinite_reverse]" />
            <div className="absolute w-2 h-2 bg-teal-400 rounded-full shadow-[0_0_10px_teal]" />
          </div>
        );
      case 'j_5': // Gambler's Insight - Eye/Dice
        return (
          <div className="relative w-full h-full flex items-center justify-center">
             <div className="w-1/2 h-1/2 bg-white/10 rounded-lg rotate-12 border border-white/20 flex items-center justify-center">
                <div className="grid grid-cols-2 gap-1 p-1">
                   <div className="w-1 h-1 bg-white rounded-full" />
                   <div className="w-1 h-1 bg-white rounded-full" />
                   <div className="w-1 h-1 bg-white rounded-full" />
                </div>
             </div>
             <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full blur-sm opacity-50" />
          </div>
        );
      case 'j_6': // Rule Bender - Glitch
        return (
          <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
             <div className="absolute w-full h-1/2 bg-cyan-400/30 -skew-x-12 translate-x-2 animate-pulse" />
             <div className="absolute w-full h-1/3 bg-fuchsia-500/30 skew-x-12 -translate-x-2 delay-100 animate-pulse" />
             <span className="text-4xl font-black italic tracking-tighter text-white animate-bounce">?!</span>
          </div>
        );
      case 'j_7': // Momentum - Arrows
        return (
          <div className="relative w-full h-full flex flex-col items-center justify-center gap-1">
             {[...Array(3)].map((_, i) => (
               <svg key={i} viewBox="0 0 24 24" className="w-1/3 h-1/3 text-emerald-400 fill-current animate-bounce" style={{ animationDelay: `${i * 0.2}s` }}>
                 <path d="M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" />
               </svg>
             ))}
          </div>
        );
      case 'j_8': // Emergency Focus - Red Cross
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="absolute w-3/4 h-3/4 bg-red-600/20 rounded-full animate-ping" />
            <svg viewBox="0 0 24 24" className="w-1/2 h-1/2 text-red-500 fill-current relative z-10">
              <path d="M12,2C11.5,2 11,2.5 11,3C11,3.5 11.5,4 12,4H14V6C14,6.5 14.5,7 15,7C15.5,7 16,6.5 16,6V4H18C18.5,4 19,3.5 19,3C19,2.5 18.5,2 18,2H16V0C16,-0.5 15.5,-1 15,-1C14.5,-1 14,-0.5 14,0V2H12M12,2L12,4M12,4V10L8,10V12L12,12V22H14V12L18,12V10L14,10V4H12Z" />
            </svg>
          </div>
        );
      case 'j_9': // Sacrificial Play - Spikes
        return (
          <div className="relative w-full h-full flex items-center justify-center">
             <div className="absolute inset-0 bg-red-900/10" />
             <svg viewBox="0 0 24 24" className="w-3/4 h-3/4 text-red-700 fill-current drop-shadow-[0_4px_10px_rgba(185,28,28,0.5)]">
               <path d="M12,2L4.5,20.29L5.21,21L12,18L18.79,21L19.5,20.29L12,2Z" />
             </svg>
          </div>
        );
      case 'j_10': // Clarity Shield - Hexagon
        return (
          <div className="relative w-full h-full flex items-center justify-center">
             <div className="grid grid-cols-2 gap-1 p-2 opacity-20">
                {[...Array(4)].map((_, i) => <div key={i} className="w-full aspect-square border border-blue-400 rotate-45" />)}
             </div>
             <svg viewBox="0 0 24 24" className="w-1/2 h-1/2 text-blue-400 fill-current relative z-10">
               <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,3L19,6.11V11C19,15.33 16.05,19.38 12,20.91C7.95,19.38 5,15.33 5,11V6.11L12,3Z" />
             </svg>
          </div>
        );
      default:
        return (
          <div className="w-full h-full flex items-center justify-center text-white/20 font-black italic">
            ?
          </div>
        );
    }
  };

  return (
    <div className={`relative overflow-hidden rounded-xl bg-black/60 border border-white/10 ${isLarge ? 'w-full aspect-[4/5] mb-6 shadow-2xl' : 'w-12 h-16 flex-shrink-0'}`}>
      {/* Background patterns based on rarity */}
      <div className={`absolute inset-0 opacity-10 pointer-events-none ${rarity === 'Rare' ? 'legendary-holo' : rarity === 'Uncommon' ? 'bg-emerald-500' : 'bg-slate-500'}`} />
      <div className="absolute inset-0 card-texture pointer-events-none opacity-40" />
      
      {/* Illustration Area */}
      <div className="absolute inset-2 flex items-center justify-center z-10">
        {renderIllustration()}
      </div>

      {/* Frame details for large cards */}
      {isLarge && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-white/20" />
          <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-white/20" />
          <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-white/20" />
          <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-white/20" />
        </div>
      )}
      
      {/* Rarity/Holo Overlay */}
      {(rarity === 'Rare' || rarity === 'Legendary') && (
        <div className="absolute inset-0 holo-foil pointer-events-none opacity-[0.15] z-20" />
      )}
    </div>
  );
};

export default JokerArt;
